// utils/crypto/keyBackup.ts 

import {
  fetchKeyBackup,
  fetchLatestKeyBackup,
  uploadKeyBackup,
} from "../../services/crypto/deviceKeyApi";

import {
  computeCanonicalDeviceId,
  getDeviceId,
} from "../common/deviceHelpers";

import {
  getDeviceIdAsync,
} from "../keyManager";

import {
  getPublicKeyPem,
  loadDecryptedPrivatePem,
  setPublicKeyPem,
  storeEncryptedPrivatePem,
} from "./keyStorageSecure";

import {
  u8ToArrayBuffer,
} from "./buffer";

import {
  computeKeyIdFromPublicPem,
} from "./keyUtils";

type KeyBackupBlob = {
  v: number;
  kdf: string;
  hash: string;
  iterations: number;
  salt_b64: string;
  iv_b64: string;
  ciphertext_b64: string;
  key_algo: string;
  public_key_pem?: string;
  key_id?: string;
};

// MARK: - Binary helpers

function toBase64(
  value: Uint8Array,
): string {
  let binary = "";

  for (
    let index = 0;
    index < value.length;
    index += 1
  ) {
    binary += String.fromCharCode(
      value[index],
    );
  }

  return btoa(binary);
}

function fromBase64(
  value: string,
): Uint8Array<ArrayBuffer> {
  const binary = atob(value);

  const buffer = new ArrayBuffer(
    binary.length,
  );

  const bytes =
    new Uint8Array(buffer);

  for (
    let index = 0;
    index < binary.length;
    index += 1
  ) {
    bytes[index] =
      binary.charCodeAt(index);
  }

  return bytes;
}

function createRandomBytes(
  length: number,
): Uint8Array<ArrayBuffer> {
  const bytes =
    new Uint8Array(
      new ArrayBuffer(length),
    );

  crypto.getRandomValues(bytes);

  return bytes;
}

// MARK: - Passphrase validation

function validatePassphrase(
  passphrase: string,
): void {
  if (passphrase.length < 8) {
    throw new Error(
      "The recovery passphrase must contain at least 8 characters.",
    );
  }

  if (
    passphrase !==
    passphrase.trim()
  ) {
    throw new Error(
      "The recovery passphrase cannot begin or end with spaces.",
    );
  }
}

// MARK: - KDF

async function deriveKey(
  passphrase: string,
  salt: Uint8Array,
  iterations: number,
): Promise<CryptoKey> {
  const passphraseBytes =
    new TextEncoder().encode(
      passphrase,
    );

  try {
    const keyMaterial =
      await crypto.subtle.importKey(
        "raw",
        u8ToArrayBuffer(
          passphraseBytes,
        ),
        "PBKDF2",
        false,
        ["deriveKey"],
      );

    return crypto.subtle.deriveKey(
      {
        name: "PBKDF2",
        salt: u8ToArrayBuffer(
          salt,
        ),
        iterations,
        hash: "SHA-256",
      },
      keyMaterial,
      {
        name: "AES-GCM",
        length: 256,
      },
      false,
      [
        "encrypt",
        "decrypt",
      ],
    );
  } finally {
    passphraseBytes.fill(0);
  }
}

// MARK: - Backup encryption

async function encryptPrivateKeyPem(
  privatePem: string,
  passphrase: string,
): Promise<KeyBackupBlob> {
  validatePassphrase(passphrase);

  const iterations = 600_000;

  const salt =
    createRandomBytes(16);

  const iv =
    createRandomBytes(12);

  const key =
    await deriveKey(
      passphrase,
      salt,
      iterations,
    );

  const plaintext =
    new TextEncoder().encode(
      privatePem,
    );

  try {
    const ciphertext =
      await crypto.subtle.encrypt(
        {
          name: "AES-GCM",
          iv: u8ToArrayBuffer(iv),
        },
        key,
        u8ToArrayBuffer(
          plaintext,
        ),
      );

    return {
      v: 1,
      kdf: "PBKDF2",
      hash: "SHA-256",
      iterations,
      salt_b64:
        toBase64(salt),
      iv_b64:
        toBase64(iv),
      ciphertext_b64:
        toBase64(
          new Uint8Array(
            ciphertext,
          ),
        ),
      key_algo:
        "RSA-2048-OAEP-SHA-256",
    };
  } finally {
    plaintext.fill(0);
  }
}

// MARK: - Backup decryption

async function decryptPrivateKeyPem(
  blob: KeyBackupBlob,
  passphrase: string,
): Promise<string> {
  validatePassphrase(passphrase);

  if (
    !blob ||
    typeof blob.ciphertext_b64 !==
      "string" ||
    typeof blob.salt_b64 !==
      "string" ||
    typeof blob.iv_b64 !==
      "string"
  ) {
    throw new Error(
      "The encrypted recovery backup is invalid.",
    );
  }

  const salt =
    fromBase64(
      blob.salt_b64,
    );

  const iv =
    fromBase64(
      blob.iv_b64,
    );

  const ciphertext =
    fromBase64(
      blob.ciphertext_b64,
    );

  const key =
    await deriveKey(
      passphrase,
      salt,
      Number(blob.iterations) ||
        600_000,
    );

  try {
    const plaintext =
      await crypto.subtle.decrypt(
        {
          name: "AES-GCM",
          iv: u8ToArrayBuffer(iv),
        },
        key,
        u8ToArrayBuffer(
          ciphertext,
        ),
      );

    return new TextDecoder().decode(
      plaintext,
    );
  } catch (error) {
    if (
      error instanceof DOMException &&
      error.name === "OperationError"
    ) {
      throw new Error(
        "The recovery passphrase is incorrect or the encrypted backup is damaged.",
      );
    }

    throw error;
  }
}

// MARK: - Restored key persistence

async function persistRestoredKeyPair(
  blob: KeyBackupBlob,
  privatePem: string,
): Promise<void> {
  if (
    typeof blob.public_key_pem !==
      "string" ||
    !blob.public_key_pem.trim()
  ) {
    throw new Error(
      "The recovery backup does not contain its public encryption key.",
    );
  }

  const publicPem =
    blob.public_key_pem.trim();

  if (
    !publicPem.includes(
      "-----BEGIN PUBLIC KEY-----",
    ) ||
    !publicPem.includes(
      "-----END PUBLIC KEY-----",
    )
  ) {
    throw new Error(
      "The public encryption key in the recovery backup is invalid.",
    );
  }

  /*
   * Persist both halves only after:
   * 1. backup decryption succeeded;
   * 2. the public key was validated.
   *
   * IndexedDB does not provide a transaction across
   * these exported helper calls, but neither key is
   * removed before this point.
   */
  await storeEncryptedPrivatePem(
    privatePem,
  );

  try {
    await setPublicKeyPem(
      publicPem,
    );
  } catch (error) {
    throw new Error(
      "The private key was restored, but its public key could not be stored. Please retry the restore operation.",
      {
        cause: error,
      },
    );
  }
}

// MARK: - Backup current key

export async function backupPrivateKey(
  passphrase: string,
): Promise<boolean> {
  validatePassphrase(passphrase);

  const privatePem =
    await loadDecryptedPrivatePem();

  const publicKeyPem =
    await getPublicKeyPem();

  if (
    !publicKeyPem ||
    !publicKeyPem.trim()
  ) {
    throw new Error(
      "The current public encryption key is missing.",
    );
  }

  const blob =
    await encryptPrivateKeyPem(
      privatePem,
      passphrase,
    );

  blob.public_key_pem =
    publicKeyPem;

  blob.key_id =
    await computeKeyIdFromPublicPem(
      publicKeyPem,
    );

  const deviceId =
    await getDeviceIdAsync();

  await uploadKeyBackup(
    deviceId,
    blob,
  );

  return true;
}

// MARK: - Restore latest account backup

export async function restorePrivateKeyLatest(
  passphrase: string,
): Promise<boolean> {
  validatePassphrase(passphrase);

  const blob =
    (await fetchLatestKeyBackup()) as
      | KeyBackupBlob
      | null;

  if (!blob) {
    throw new Error(
      "No encrypted recovery backup was found.",
    );
  }

  const privatePem =
    await decryptPrivateKeyPem(
      blob,
      passphrase,
    );

  await persistRestoredKeyPair(
    blob,
    privatePem,
  );

  try {
    await computeCanonicalDeviceId();
  } catch {
    // Registration will resolve and mirror
    // the canonical device ID again.
  }

  return true;
}

// MARK: - Restore per-device backup

export async function restorePrivateKey(
  passphrase: string,
): Promise<boolean> {
  validatePassphrase(passphrase);

  const deviceId =
    getDeviceId();

  if (
    !deviceId ||
    deviceId === "unknown"
  ) {
    throw new Error(
      "No local device identifier is available. Restore the latest account backup instead.",
    );
  }

  const blob =
    (await fetchKeyBackup(
      deviceId,
    )) as
      | KeyBackupBlob
      | null;

  if (!blob) {
    throw new Error(
      "No encrypted recovery backup was found for this device.",
    );
  }

  const privatePem =
    await decryptPrivateKeyPem(
      blob,
      passphrase,
    );

  await persistRestoredKeyPair(
    blob,
    privatePem,
  );

  try {
    await computeCanonicalDeviceId();
  } catch {
    // Best effort. Registration will resolve
    // the canonical identifier again.
  }

  return true;
}